import {
  RuleFolder,
  Loop,
  Downloading,
  SupervisedUserCircle,
  Work,
  FactCheck,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Error404Page from '../components/Error404Page';
import Approvals from '../containers/approvals';
import BulkExport from '../containers/bulkExport';
import DealerShipSummaryReport from '../containers/dealershipSummaryReport';
import OrderDetails from '../containers/orderDetails';
import Orders from '../containers/orders';
import NewOrder from '../containers/orders/newOrder';
import { NewServiceOrder } from '../containers/orders/newServiceOrder';
import Users from '../containers/users';
import WorkQueue from '../containers/workQueue';
import useVendorContracts from '../hooks/useVendorContracts';
import { UserRoles } from './enums/enums';

export const DealerRoutes = userRoles => {
  const { t } = useTranslation('global');
  const { hasReconditioning, isLoading } = useVendorContracts();

  const routes = [
    {
      path: '/orders',
      component: Orders,
      name: t((hasReconditioning && 'menu.orders') || 'menu.orders&Approvals'),
      icon: (hasReconditioning && <Work />) || <RuleFolder />,
      toolbar: true,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t(
        (hasReconditioning && 'breadCrumbs.orders') ||
          'breadCrumbs.orders&Approvals'
      ),
      'data-test': 'main-menu-btn',
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    },
    {
      path: '/orders/new',
      component: NewOrder,
      toolbar: false,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.newOrder'),
      'data-test': 'main-menu-btn',
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    },
    {
      path: '/orders/newServiceOrder',
      component: NewServiceOrder,
      toolbar: false,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.newServiceOrder'),
      'data-test': 'main-menu-btn',
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    },
    {
      path: '/orders/:vendorOrderId([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})',
      component: OrderDetails,
      name: t('menu.workOrdersDetail'),
      toolbar: false,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.workOrdersDetail'),
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    },
    {
      path: '/workQueue',
      component: WorkQueue,
      name: t('menu.workQueue'),
      icon: <Loop />,
      toolbar: true,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.workQueue'),
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    },
    {
      path: '/workQueue/orders/:vendorOrderId',
      component: OrderDetails,
      name: t('menu.workOrdersDetail'),
      toolbar: false,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.workOrdersDetail'),
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    },
    {
      path: '/users',
      component: Users,
      name: t('menu.users'),
      icon: <SupervisedUserCircle />,
      toolbar: true,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.users'),
      allowedRoles: [UserRoles.DEALERSHIP_ADMIN],
    },
    {
      path: '/bulkExport',
      component: BulkExport,
      name: t('menu.bulkExport'),
      icon: <Downloading />,
      toolbar: true,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.bulkExport'),
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    },
    {
      path: '/dealership-summary-report',
      component: DealerShipSummaryReport,
      name: t('menu.laFontaineReport'),
      icon: <FactCheck />,
      toolbar: true,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.laFontaineReport'),
      allowedRoles: [UserRoles.DEALERSHIP_MANAGER],
    },
    {
      path: '*',
      component: Error404Page,
      toolbar: false,
      activeWhenExact: false,
      divider: false,
      breadCrumb: false,
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
        UserRoles.DEALERSHIP_ADMIN,
      ],
    },
  ];

  const redirects = [
    {
      path: '/',
      breadCrumb: t('breadCrumbs.home'),
      redirect: '/orders',
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
        (/app-dealer.diamonddigitalpro.com/i.test(window.location.href) &&
          UserRoles.DEALERSHIP_ADMIN) ||
          null,
      ],
    },
    {
      path: '/',
      breadCrumb: t('breadCrumbs.home'),
      redirect: '/users',
      allowedRoles: [UserRoles.DEALERSHIP_ADMIN],
    },
  ];

  const routesByRoles = routes.filter(route =>
    route.allowedRoles.some(routeRole => userRoles?.includes(routeRole))
  );

  const redirect = redirects.find(route =>
    route.allowedRoles.some(routeRole => userRoles?.includes(routeRole))
  );

  isLoading &&
    routesByRoles.splice(2, 0, {
      path: '/approvals',
      component: Approvals,
      toolbar: false,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.approvals'),
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    });

  hasReconditioning &&
    routesByRoles.splice(2, 0, {
      path: '/approvals',
      component: Approvals,
      name: t('menu.approvals'),
      icon: <RuleFolder />,
      toolbar: true,
      activeWhenExact: false,
      divider: false,
      breadCrumb: t('breadCrumbs.approvals'),
      allowedRoles: [
        UserRoles.DEALERSHIP_EMPLOYEE,
        UserRoles.DEALERSHIP_MANAGER,
        UserRoles.DEALERSHIP_BILLING,
      ],
    });

  redirect && routesByRoles.unshift(redirect);

  return routesByRoles;
};
